import { useEffect, useState, useRef, useCallback } from 'react';
import { MODE } from '@grcs/views/shared/components/Homework/HomeworkPreview';
import { useData } from '@grcs/context/DataContext';
import { useHomeworkOperations } from '@grcs/hook/useDatabase';

type PageTimeHook = {
  userToken?: string;
  homeworkToken?: string;
  mode: MODE;
};

export const usePageTime = ({
  userToken,
  homeworkToken,
  mode,
}: PageTimeHook) => {
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [isIdle, setIsIdle] = useState<boolean>(false);

  // Refs for tracking activity and scroll
  const lastActivity = useRef<number>(Date.now());
  const lastScrollPosition = useRef<number>(0);
  const periodicSaveInterval = useRef<NodeJS.Timeout | null>(null);
  const idleCheckInterval = useRef<NodeJS.Timeout | null>(null);

  const homeworkOps = useHomeworkOperations();

  // Constants for time tracking
  const PERIODIC_SAVE_INTERVAL = 30000; // 30 seconds
  const IDLE_THRESHOLD = 180000; // 3 minutes
  const IDLE_CHECK_INTERVAL = 10000; // Check every 10 seconds

  // Enhanced database update function
  const updateDatabase = useCallback(
    async (
      newDuration: number,
      userToken?: string,
      homeworkToken?: string,
      reason: 'visibility' | 'periodic' | 'unmount' | 'idle' = 'periodic',
    ) => {
      try {
        if (!userToken || !homeworkToken || mode !== MODE.READING) return;

        // Skip update if duration is too small (less than 1 second)
        if (newDuration < 1000) return;

        console.log(
          `📊 Updating reading time: +${Math.round(
            newDuration / 1000,
          )}s (${reason})`,
        );

        // Fetch the current duration from the database
        const { data } =
          await homeworkOps.getAssignedHomeworkByStudentIdAndHomeworkId(
            userToken,
            homeworkToken,
          );

        if (data && data.length > 0) {
          const updatedTotalDuration = data[0].total_time + newDuration;
          await homeworkOps.updateViewTimesByStudentIdAndHomeworkId(
            userToken,
            homeworkToken,
            updatedTotalDuration,
          );
          console.log(
            `✅ Total reading time updated: ${Math.round(
              updatedTotalDuration / 1000,
            )}s`,
          );
        }
      } catch (error) {
        console.error('Failed to update reading time to database:', error);
      }
    },
    [homeworkOps, mode],
  );

  // Activity detection functions
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivity.current = now;

    // Resume timing if we were idle
    if (isIdle && !startTime) {
      console.log('🔄 Resuming reading timer after activity');
      setIsIdle(false);
      setStartTime(new Date());
    }
  }, [isIdle, startTime]);

  // Scroll tracking for engagement
  const handleScroll = useCallback(() => {
    const currentScrollPosition = window.pageYOffset;
    const scrollDelta = Math.abs(
      currentScrollPosition - lastScrollPosition.current,
    );

    // Only count as activity if significant scroll (more than 50px)
    if (scrollDelta > 50) {
      lastScrollPosition.current = currentScrollPosition;
      updateActivity();
    }
  }, [updateActivity]);

  useEffect(() => {
    setStartTime(new Date());
  }, []);

  useEffect(() => {
    if (!userToken || !homeworkToken || mode !== MODE.READING) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && startTime && !isIdle) {
        const endTime = new Date();
        const duration = endTime.getTime() - startTime.getTime();
        console.log('👀 Tab hidden, saving reading time');
        setTotalDuration((prevDuration) => prevDuration + duration);
        updateDatabase(duration, userToken, homeworkToken, 'visibility');
        setStartTime(null);
      } else if (document.visibilityState === 'visible' && !isIdle) {
        console.log('👀 Tab visible, resuming reading timer');
        setStartTime(new Date());
        updateActivity();
      }
    };

    // Periodic save function
    const performPeriodicSave = () => {
      if (startTime && !isIdle && document.visibilityState === 'visible') {
        const endTime = new Date();
        const duration = endTime.getTime() - startTime.getTime();
        console.log('💾 Periodic save triggered');
        setTotalDuration((prevDuration) => prevDuration + duration);
        updateDatabase(duration, userToken, homeworkToken, 'periodic');
        setStartTime(new Date()); // Reset start time for next interval
      }
    };

    // Idle detection function
    const checkForIdle = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity.current;

      if (timeSinceLastActivity > IDLE_THRESHOLD && !isIdle && startTime) {
        console.log('😴 User appears to be idle, pausing reading timer');
        const duration = startTime.getTime() - (now - timeSinceLastActivity);
        if (duration > 0) {
          setTotalDuration((prevDuration) => prevDuration + duration);
          updateDatabase(duration, userToken, homeworkToken, 'idle');
        }
        setIsIdle(true);
        setStartTime(null);
      }
    };

    // Set up event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousemove', updateActivity, { passive: true });
    document.addEventListener('keydown', updateActivity);
    document.addEventListener('click', updateActivity);

    // Set up periodic save interval
    periodicSaveInterval.current = setInterval(
      performPeriodicSave,
      PERIODIC_SAVE_INTERVAL,
    );

    // Set up idle check interval
    idleCheckInterval.current = setInterval(checkForIdle, IDLE_CHECK_INTERVAL);

    console.log('📚 Reading time tracking started');

    return () => {
      // Final save on cleanup
      if (startTime && !isIdle) {
        const endTime = new Date();
        const duration = endTime.getTime() - startTime.getTime();
        console.log('🏁 Component unmounting, final save');
        setTotalDuration((prevDuration) => prevDuration + duration);
        updateDatabase(duration, userToken, homeworkToken, 'unmount');
      }

      // Clean up event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousemove', updateActivity);
      document.removeEventListener('keydown', updateActivity);
      document.removeEventListener('click', updateActivity);

      // Clear intervals
      if (periodicSaveInterval.current) {
        clearInterval(periodicSaveInterval.current);
        periodicSaveInterval.current = null;
      }
      if (idleCheckInterval.current) {
        clearInterval(idleCheckInterval.current);
        idleCheckInterval.current = null;
      }

      console.log('📚 Reading time tracking stopped');
    };
  }, [
    updateDatabase,
    handleScroll,
    updateActivity,
    userToken,
    homeworkToken,
    mode,
    startTime,
    isIdle,
  ]);

  return totalDuration;
};
